const CPF_FORMATTING = /[.\-/]/g;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_FORMATTING = /[\s()+.\-]/g;
const PHONE_ALLOWED = /^[\d\s()+.\-]+$/;

export function isValidDateOfBirth(value: string): boolean {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() <= Date.now();
}

export function isValidEnrollmentDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00`);
  return !Number.isNaN(date.getTime()) && date.getTime() <= Date.now();
}

export function isValidCpf(value: string): boolean {
  const cpf = value.trim().replace(CPF_FORMATTING, "");
  if (!/^\d{11}$/.test(cpf) || new Set(cpf).size === 1) return false;

  const computeCheckDigit = (digits: string, initialWeight: number): number => {
    const sum = [...digits].reduce((total, digit, index) => total + Number(digit) * (initialWeight - index), 0);
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  return computeCheckDigit(cpf.slice(0, 9), 10) === Number(cpf[9])
    && computeCheckDigit(cpf.slice(0, 10), 11) === Number(cpf[10]);
}

export function isValidEmail(value: string): boolean {
  const email = value.trim();
  if (email.length === 0 || email.length > 254 || !EMAIL_PATTERN.test(email)) return false;
  const [localPart, domain] = email.split("@");
  if (!localPart || !domain || localPart.startsWith(".") || localPart.endsWith(".") || localPart.includes("..")) return false;
  return domain.split(".").every(label => label.length > 0 && !label.startsWith("-") && !label.endsWith("-"));
}

export function isValidPhone(value: string): boolean {
  const phone = value.trim();
  if (!PHONE_ALLOWED.test(phone)) return false;
  let digits = phone.replace(PHONE_FORMATTING, "");
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) digits = digits.slice(2);

  const hasValidLength = digits.length === 10 || digits.length === 11;
  const hasValidAreaCode = hasValidLength && digits[0] !== "0" && digits[1] !== "0";
  const hasValidMobilePrefix = digits.length !== 11 || digits[2] === "9";
  return hasValidLength && hasValidAreaCode && hasValidMobilePrefix && new Set(digits).size > 1;
}

export const MAX_GUARDIANS = 10;

const BLOOD_TYPES = new Set(["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"]);
const BRAZILIAN_STATES = new Set([
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG",
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
]);

export function isValidBloodType(value: string): boolean {
  return value === "" || BLOOD_TYPES.has(value);
}

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isValidWeightKg(value: string): boolean {
  if (value.trim() === "") return true;
  const parsed = parseOptionalNumber(value);
  return parsed !== null && parsed > 0 && parsed <= 500;
}

export function isValidHeightCm(value: string): boolean {
  if (value.trim() === "") return true;
  const parsed = parseOptionalNumber(value);
  return parsed !== null && parsed > 0 && parsed <= 300;
}

export function isValidZipCode(value: string): boolean {
  const trimmed = value.trim();
  return trimmed === "" || /^\d{8}$/.test(trimmed) || /^\d{5}-\d{3}$/.test(trimmed);
}

export function normalizeState(value: string): string {
  return value.trim().toUpperCase();
}

export function isValidState(value: string): boolean {
  const normalized = normalizeState(value);
  return normalized === "" || BRAZILIAN_STATES.has(normalized);
}

export const MAX_NOTES_LENGTH = 1000;

export function isValidNotes(value: string): boolean {
  return value.length <= MAX_NOTES_LENGTH;
}

export function isValidHealthNotes(value: string): boolean {
  return isValidNotes(value);
}

export function isValidGuardianName(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= 120;
}
